import { useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

/**
 * The rendered width of the widest of `labels`, in px, measured in the font of `element`.
 *
 * A label that resizes its own pill drags the pill's right edge across whatever sits under it,
 * the pointer included. On the Feature pill that closed a loop: hovering paused the cycling,
 * which shortened the label, which moved the edge off the pointer, which resumed it — the pill
 * flickered several times a second (Jeff, 2026-10-23). Freezing on hover broke the loop, but
 * the 142–196px swing is what made it constructible, so the pill reserves the widest width and
 * stops moving at all.
 *
 * Measured with `measureText` rather than by rendering every candidate: a stack of hidden
 * copies would reserve the width too, but it also puts every label into the pill's `textContent`,
 * where it muddies the accessible name and lets a test assert a label that is merely the
 * invisible sizer.
 *
 * Returns `undefined` until it can measure — on the server (the layout effect is isomorphic,
 * so it simply does not run there), and anywhere `2d` canvas is
 * unavailable (jsdom) — so the caller simply reserves nothing and the pill hugs its label.
 */
export const useWidestLabelWidth = (labels: ReadonlyArray<string>, element: HTMLElement | null): number | undefined => {
  const [width, setWidth] = useState<number>();
  const key = labels.join('|');
  // `labels` is rebuilt every render; the joined key is what actually decides a re-measure.
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  useIsomorphicLayoutEffect(() => {
    const current = labelsRef.current;
    if (!element || current.length < 2) {
      setWidth(undefined);
      return;
    }
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return;
    // Firefox leaves the `font` shorthand empty when the styles were set as longhands, which
    // would silently leave the canvas on its 10px default and under-measure every label.
    const style = window.getComputedStyle(element);
    context.font = style.font || `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const widest = current.reduce((max, label) => Math.max(max, context.measureText(label).width), 0);
    setWidth(widest > 0 ? Math.ceil(widest) : undefined);
  }, [key, element]);

  return width;
};
